import type { AchievementView, LeaderboardInfo, LeaderboardView } from '@mogzo/shared';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { type UiTokens, withAlpha } from './look';
import type { MultiplayerData } from './match';

export type BoardInfo = Omit<LeaderboardInfo, 'period'>;
export type BoardViewName = 'top' | 'around';
export type BoardPeriod = 'current' | 'previous';

/** Where the panels get the game's scores and achievements (the play API, or sample data). */
export interface OverlayData {
  boards(): Promise<BoardInfo[]>;
  leaderboard(
    id: string,
    view: BoardViewName,
    period: BoardPeriod,
    limit: number,
  ): Promise<LeaderboardView>;
  achievements(): Promise<AchievementView[]>;
  /** Matches for the multiplayer panel (M31); missing where there's no multiplayer. */
  multiplayer?: MultiplayerData;
}

/** The player's choices in the pause menu, kept per game on their device. */
export interface PlayerSettings {
  sound: boolean;
  popups: boolean;
  /** Match chat's lines and notes over the game (M38b); on unless turned off. */
  chat?: boolean;
}

/** A row in the pause menu that opens a chat, with how many messages are new. */
export interface PauseChatRow {
  unread: number;
  open: () => void;
}

/**
 * A score as people read it: `12,850`, or for time boards (milliseconds) `1:02.345`. The same as
 * @mogzo/shared's formatScore, kept here so the mogzo dev bundle doesn't pull in zod.
 */
function formatScore(score: number, format: 'number' | 'time'): string {
  if (format === 'number') return score.toLocaleString('en');
  const ms = Math.max(0, Math.round(score));
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0')}`;
}

/** What a load answers with: nothing yet, the data, or a failure. */
type Loaded<T> = { state: 'loading' } | { state: 'ready'; value: T } | { state: 'failed' };

/** Loads again whenever `source` or `key` changes. */
function useLoad<T>(load: () => Promise<T>, source: OverlayData, key: string): Loaded<T> {
  const [loaded, setLoaded] = useState<Loaded<T>>({ state: 'loading' });
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` names what the load depends on
  useEffect(() => {
    let active = true;
    setLoaded({ state: 'loading' });
    load()
      .then((value) => active && setLoaded({ state: 'ready', value }))
      .catch(() => active && setLoaded({ state: 'failed' }));
    return () => {
      active = false;
    };
  }, [source, key]);
  return loaded;
}

function Icon({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const TROPHY = (
  <>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </>
);
const CHAT_BUBBLE = <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />;
const MESSAGES_ICON = (
  <>
    <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
    <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
  </>
);

const AWARD = (
  <>
    <circle cx="12" cy="8" r="6" />
    <path d="M15.48 12.89 17 22l-5-3-5 3 1.52-9.11" />
  </>
);
const PERSON = (
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </>
);

/** The close button of a panel. */
function Close({
  t,
  onClose,
  label = 'Close',
}: {
  t: UiTokens;
  onClose: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={label}
      style={{
        flex: 'none',
        width: 44,
        height: 44,
        display: 'grid',
        placeItems: 'center',
        border: 0,
        borderRadius: t.innerRadius,
        background: t.colours.field,
        color: t.colours.text,
        cursor: 'pointer',
      }}
    >
      <Icon size={16}>
        <path d="M6 6l12 12M18 6L6 18" />
      </Icon>
    </button>
  );
}

/** Two or more choices in a pill, such as Top / Around me. */
function Choice<T extends string>({
  t,
  label,
  value,
  options,
  onChange,
}: {
  t: UiTokens;
  label: string;
  value: T;
  options: [T, string][];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset
      style={{
        display: 'flex',
        margin: 0,
        padding: 3,
        border: 0,
        borderRadius: t.buttonRadius,
        background: t.colours.field,
      }}
    >
      <legend
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {label}
      </legend>
      {options.map(([option, text]) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          style={{
            minHeight: 36,
            padding: '0 14px',
            border: 0,
            borderRadius: t.buttonRadius,
            background: value === option ? t.colours.text : 'transparent',
            color: value === option ? t.colours.panel : t.colours.text,
            font: 'inherit',
            fontSize: '0.9em',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {text}
        </button>
      ))}
    </fieldset>
  );
}

const PERIODS: Record<BoardInfo['reset'], [string, string] | null> = {
  none: null,
  daily: ['Today', 'Yesterday'],
  weekly: ['This week', 'Last week'],
  monthly: ['This month', 'Last month'],
};
const RESETS: Record<BoardInfo['reset'], string> = {
  none: 'All time',
  daily: 'Resets every day at 00:00 UTC',
  weekly: 'Resets on Monday at 00:00 UTC',
  monthly: 'Resets on the 1st at 00:00 UTC',
};

function ordinal(n: number): string {
  const tens = n % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? 'th'
      : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th');
  return `${n}${suffix}`;
}

/** The leaderboard: Top or Around me, this period or the last; the list scrolls. */
export function LeaderboardPanel({
  t,
  data,
  initial,
  onClose,
}: {
  t: UiTokens;
  data: OverlayData;
  initial: { id?: string; view?: BoardViewName; period?: BoardPeriod };
  onClose: () => void;
}) {
  const boards = useLoad(() => data.boards(), data, 'boards');
  const [chosen, setChosen] = useState(initial.id);
  const [view, setView] = useState<BoardViewName>(initial.view ?? 'top');
  const [period, setPeriod] = useState<BoardPeriod>(initial.period ?? 'current');
  const list = boards.state === 'ready' ? boards.value : [];
  const board = list.find((b) => b.id === chosen) ?? list[0];
  const entries = useLoad(
    () => (board ? data.leaderboard(board.id, view, period, 50) : Promise.resolve(null)),
    data,
    `${board?.id}/${view}/${period}`,
  );
  const mine = useLoad(
    () => (board ? data.leaderboard(board.id, 'around', period, 1) : Promise.resolve(null)),
    data,
    `${board?.id}/${period}`,
  );
  const me = mine.state === 'ready' ? mine.value?.entries.find((entry) => entry.me) : undefined;
  const periods = board ? PERIODS[board.reset] : null;
  const badge = Math.min(30, t.values.rowHeight);
  const highlight = t.values.badges === 'accent';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="mogzo-panel-title" style={{ margin: 0, fontSize: '1.3em', fontWeight: 700 }}>
            {board?.name ?? 'Leaderboard'}
          </h2>
          {board && (
            <p style={{ margin: '2px 0 0', fontSize: '0.87em', color: t.colours.muted }}>
              {RESETS[board.reset]}
            </p>
          )}
        </div>
        <Close t={t} onClose={onClose} />
      </div>

      {list.length > 1 && board && (
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: '0.87em',
            color: t.colours.muted,
          }}
        >
          Board
          <select
            value={board.id}
            onChange={(event) => setChosen(event.target.value)}
            style={{
              minHeight: 40,
              padding: '0 10px',
              border: `1px solid ${t.colours.line}`,
              borderRadius: t.innerRadius,
              background: t.colours.field,
              color: t.colours.text,
              font: 'inherit',
              fontSize: '1.07em',
            }}
          >
            {list.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {board && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <Choice
            t={t}
            label="Show"
            value={view}
            options={[
              ['top', 'Top'],
              ['around', 'Around me'],
            ]}
            onChange={setView}
          />
          {periods && (
            <Choice
              t={t}
              label="When"
              value={period}
              options={[
                ['current', periods[0]],
                ['previous', periods[1]],
              ]}
              onChange={setPeriod}
            />
          )}
        </div>
      )}

      {boards.state === 'ready' && list.length === 0 && (
        <p style={{ margin: 0, color: t.colours.muted }}>This game has no leaderboard yet.</p>
      )}
      {(boards.state === 'failed' || entries.state === 'failed') && (
        <p style={{ margin: 0, color: t.colours.muted }}>
          Couldn’t load the leaderboard. Try again in a moment.
        </p>
      )}
      {board && entries.state === 'loading' && (
        <p role="status" style={{ margin: 0, color: t.colours.muted }}>
          Loading scores…
        </p>
      )}
      {board && entries.state === 'ready' && entries.value?.entries.length === 0 && (
        <p style={{ margin: 0, color: t.colours.muted }}>No scores yet. Be the first.</p>
      )}
      {board && entries.state === 'ready' && entries.value && entries.value.entries.length > 0 && (
        <ol aria-label="Scores" style={t.list}>
          {entries.value.entries.map((entry) => (
            <li
              key={`${entry.rank}-${entry.playerId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: t.values.rowHeight,
                padding: '0 8px',
                borderRadius: t.innerRadius,
                background: entry.me ? withAlpha(t.colours.accent, 0.22) : 'transparent',
              }}
            >
              <span
                style={{
                  flex: 'none',
                  minWidth: badge,
                  height: badge,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 999,
                  background: entry.rank <= 3 && highlight ? t.colours.accent : 'transparent',
                  color: entry.rank <= 3 && highlight ? t.colours.onAccent : t.colours.muted,
                  fontSize: '0.87em',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {entry.rank}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: entry.me ? 700 : 500,
                }}
              >
                {entry.me ? `${entry.name} (you)` : entry.name}
              </span>
              <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatScore(entry.score, board.format)}
              </span>
            </li>
          ))}
        </ol>
      )}

      {board && (
        <p
          style={{
            margin: 0,
            paddingTop: t.gap,
            borderTop: `1px solid ${t.colours.line}`,
            fontSize: '0.93em',
            fontWeight: 700,
          }}
        >
          {me
            ? `Your best: ${formatScore(me.score, board.format)} · ${ordinal(me.rank)}`
            : 'You’re not on this board yet.'}
        </p>
      )}
    </>
  );
}

/** The game's achievements, with progress; hidden ones stay secret until earned. */
export function AchievementsPanel({
  t,
  data,
  onClose,
}: {
  t: UiTokens;
  data: OverlayData;
  onClose: () => void;
}) {
  const loaded = useLoad(() => data.achievements(), data, 'achievements');
  const all = loaded.state === 'ready' ? loaded.value : [];
  const unlocked = all.filter((a) => a.unlocked);
  const points = unlocked.reduce((sum, a) => sum + a.points, 0);
  const total = all.reduce((sum, a) => sum + a.points, 0);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="mogzo-panel-title" style={{ margin: 0, fontSize: '1.3em', fontWeight: 700 }}>
            Achievements
          </h2>
          {loaded.state === 'ready' && all.length > 0 && (
            <p style={{ margin: '2px 0 0', fontSize: '0.87em', color: t.colours.muted }}>
              {unlocked.length} of {all.length} unlocked · {points} of {total} points
            </p>
          )}
        </div>
        <Close t={t} onClose={onClose} />
      </div>
      {loaded.state === 'loading' && (
        <p role="status" style={{ margin: 0, color: t.colours.muted }}>
          Loading achievements…
        </p>
      )}
      {loaded.state === 'failed' && (
        <p style={{ margin: 0, color: t.colours.muted }}>
          Couldn’t load achievements. Try again in a moment.
        </p>
      )}
      {loaded.state === 'ready' && all.length === 0 && (
        <p style={{ margin: 0, color: t.colours.muted }}>This game has no achievements yet.</p>
      )}
      {all.length > 0 && (
        <ul
          aria-label="Achievements"
          style={{ ...t.list, display: 'flex', flexDirection: 'column', gap: t.gap }}
        >
          {all.map((a) => (
            <li
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 10,
                borderRadius: t.innerRadius,
                background: a.unlocked ? t.colours.field : 'transparent',
                border: a.unlocked ? '1px solid transparent' : `1px solid ${t.colours.line}`,
              }}
            >
              <AchievementIcon t={t} achievement={a} size={t.values.iconSize} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{a.name}</strong>
                {a.description && (
                  <span style={{ display: 'block', fontSize: '0.87em', color: t.colours.muted }}>
                    {a.description}
                  </span>
                )}
                {a.steps && !a.unlocked && (
                  <>
                    <span style={{ display: 'block', fontSize: '0.87em', color: t.colours.muted }}>
                      {Math.min(a.progress, a.steps)} of {a.steps}
                    </span>
                    <span
                      role="progressbar"
                      aria-label={`${a.name} progress`}
                      aria-valuemin={0}
                      aria-valuemax={a.steps}
                      aria-valuenow={Math.min(a.progress, a.steps)}
                      style={{ ...t.progressTrack, marginTop: 6 }}
                    >
                      <span
                        style={{
                          display: 'block',
                          width: `${Math.round((Math.min(a.progress, a.steps) / a.steps) * 100)}%`,
                          height: '100%',
                          background: t.colours.accent,
                        }}
                      />
                    </span>
                  </>
                )}
              </span>
              <span
                style={{
                  flex: 'none',
                  fontSize: '0.87em',
                  fontWeight: 700,
                  color: a.unlocked ? t.colours.text : t.colours.muted,
                }}
              >
                {a.points} pts
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** An achievement's picture, or MOGZO's plain badge; dimmed until earned. */
export function AchievementIcon({
  t,
  achievement,
  size,
}: {
  t: UiTokens;
  achievement: AchievementView;
  size: number;
}) {
  const style: CSSProperties = {
    flex: 'none',
    width: size,
    height: size,
    borderRadius: t.innerRadius,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    background: achievement.icon ? 'transparent' : t.colours.accent,
    color: t.colours.onAccent,
    opacity: achievement.unlocked ? 1 : 0.45,
  };
  return (
    <span style={style}>
      {achievement.icon ? (
        <img
          src={achievement.icon}
          alt=""
          width={size}
          height={size}
          style={{ display: 'block', width: size, height: size }}
        />
      ) : (
        <Icon size={Math.round(size / 2)}>{AWARD}</Icon>
      )}
    </span>
  );
}

/** MOGZO's pause menu: back to the game, the leaderboard and achievements, settings. */
export function PauseMenu({
  t,
  gameName,
  settings,
  onSettings,
  onResume,
  onOpen,
  onRestart,
  onExit,
  matchChat,
  messages,
}: {
  t: UiTokens;
  gameName: string;
  settings: PlayerSettings;
  onSettings: (next: PlayerSettings) => void;
  onResume: () => void;
  onOpen: (panel: 'leaderboard' | 'achievements') => void;
  onRestart?: () => void;
  onExit?: () => void;
  /** The match's chat (M38b), when there is one. */
  matchChat?: PauseChatRow | null;
  /** The player's own messages (mogzo.com, M38b). */
  messages?: PauseChatRow | null;
}) {
  const tile: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
    padding: Math.max(10, t.padding - 6),
    border: 0,
    borderRadius: t.innerRadius,
    background: t.colours.field,
    color: t.colours.text,
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  };
  const chatRow = (row: PauseChatRow, label: string, icon: ReactNode) => (
    <button
      type="button"
      onClick={row.open}
      style={{ ...tile, flexDirection: 'row', alignItems: 'center', gap: 12, width: '100%' }}
    >
      <Icon size={20}>{icon}</Icon>
      <strong style={{ flex: 1 }}>{label}</strong>
      {row.unread > 0 && (
        <span
          style={{
            minWidth: 20,
            padding: '2px 7px',
            borderRadius: 999,
            background: t.colours.accent,
            color: t.colours.onAccent,
            fontSize: 12,
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {row.unread}
          <span
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
            }}
          >
            {' '}
            new
          </span>
        </span>
      )}
    </button>
  );
  const toggle = (key: keyof PlayerSettings, label: string) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: 48,
      }}
    >
      <span id={`mogzo-setting-${key}`} style={{ fontWeight: 600 }}>
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={settings[key] !== false}
        aria-labelledby={`mogzo-setting-${key}`}
        onClick={() => onSettings({ ...settings, [key]: settings[key] === false })}
        style={t.switchTrack(settings[key] !== false)}
      >
        <span style={t.switchKnob(settings[key] !== false)} />
      </button>
    </div>
  );
  return (
    <>
      <div>
        <h2 id="mogzo-panel-title" style={{ margin: 0, fontSize: '1.3em', fontWeight: 700 }}>
          Paused
        </h2>
        <p style={{ margin: 0, fontSize: '0.93em', color: t.colours.muted }}>{gameName}</p>
      </div>
      <button
        type="button"
        onClick={onResume}
        style={{ ...t.primary, width: '100%' }}
        data-autofocus
      >
        Resume
      </button>
      {matchChat && chatRow(matchChat, 'Match chat', CHAT_BUBBLE)}
      {messages && chatRow(messages, 'Your messages', MESSAGES_ICON)}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: t.gap }}
      >
        <button type="button" onClick={() => onOpen('leaderboard')} style={tile}>
          <Icon size={22}>{TROPHY}</Icon>
          <strong>Leaderboard</strong>
        </button>
        <button type="button" onClick={() => onOpen('achievements')} style={tile}>
          <Icon size={22}>{AWARD}</Icon>
          <strong>Achievements</strong>
        </button>
      </div>
      <div
        style={{
          borderTop: `1px solid ${t.colours.line}`,
          borderBottom: `1px solid ${t.colours.line}`,
        }}
      >
        {toggle('sound', 'Sound')}
        {toggle('popups', 'Achievement pop-ups')}
        {matchChat && toggle('chat', 'Show chat over the game')}
      </div>
      {(onRestart || onExit) && (
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: t.gap }}
        >
          {onRestart && (
            <button type="button" onClick={onRestart} style={t.secondary}>
              Restart
            </button>
          )}
          {onExit && (
            <button type="button" onClick={onExit} style={t.secondary}>
              Exit game
            </button>
          )}
        </div>
      )}
    </>
  );
}

/** Asks a guest to sign in, so their progress is kept. */
export function SignInPrompt({
  t,
  signInUrl,
  signUpUrl,
  onClose,
}: {
  t: UiTokens;
  signInUrl: string;
  signUpUrl: string;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: t.gap,
        textAlign: 'center',
      }}
    >
      <span
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          background: t.colours.accent,
          color: t.colours.onAccent,
        }}
      >
        <Icon size={26}>{PERSON}</Icon>
      </span>
      <h2 id="mogzo-panel-title" style={{ margin: 0, fontSize: '1.4em', fontWeight: 700 }}>
        Keep your progress
      </h2>
      <p style={{ margin: 0, maxWidth: '32ch', color: t.colours.muted }}>
        You’re playing as a guest. Sign in to keep your best score and achievements on every device,
        and to get on the leaderboard.
      </p>
      <a
        href={signInUrl}
        target="_top"
        style={{ ...t.primary, width: '100%', boxSizing: 'border-box' }}
        data-autofocus
      >
        Sign in
      </a>
      <button type="button" onClick={onClose} style={{ ...t.quiet, width: '100%' }}>
        Not now
      </button>
      <p style={{ margin: 0, fontSize: '0.87em', color: t.colours.muted }}>
        New to MOGZO?{' '}
        <a href={signUpUrl} target="_top" style={{ color: t.colours.text, fontWeight: 700 }}>
          Make an account
        </a>
      </p>
    </div>
  );
}
